import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import type { Question } from '../../../content';
import type { SessionResult } from '../sessionSummaryData';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../../app/hooks/useBodyScrollLock';
/**
 * ReviewAnswersScreen — full-screen overlay for reviewing all session answers (§20.3 G2).
 *
 * - Shows every question in order, marked correct (✓) or incorrect (✕)
 * - Incorrect answers are expanded by default; correct ones are collapsed and tappable
 * - Sticky footer with "Terug naar samenvatting"
 * - Optional initialIndex scrolls to a specific question when launched from a ScoreDot
 */
interface ReviewAnswersScreenProps {
  sessionAnswers: SessionResult[];
  sessionQuestions: Question[];
  initialIndex?: number;
  onBack: () => void;
}
/** Defensive extraction of a human-readable correct answer from a Question. */
function getCorrectAnswerText(q: Question | undefined): string | null {
  if (!q) return null;
  const anyQ = q as any;
  // Multiple choice with options + correctIndex/correctOption
  if (Array.isArray(anyQ.options)) {
    if (typeof anyQ.correctIndex === 'number' && anyQ.options[anyQ.correctIndex]) {
      const opt = anyQ.options[anyQ.correctIndex];
      return typeof opt === 'string' ? opt : opt?.text ?? opt?.label ?? null;
    }
    if (anyQ.correctOption && typeof anyQ.correctOption === 'string') {
      return anyQ.correctOption;
    }
  }
  // Direct answer fields
  if (typeof anyQ.correctAnswer === 'string') return anyQ.correctAnswer;
  if (typeof anyQ.correctAnswer === 'number') return String(anyQ.correctAnswer);
  if (Array.isArray(anyQ.correctAnswer)) return anyQ.correctAnswer.join(', ');
  if (typeof anyQ.answer === 'string') return anyQ.answer;
  if (typeof anyQ.answer === 'number') return String(anyQ.answer);
  // Fill-in style
  if (typeof anyQ.solution === 'string') return anyQ.solution;
  return null;
}
function getQuestionText(q: Question | undefined): string {
  if (!q) return 'Vraag niet beschikbaar';
  const anyQ = q as any;
  return anyQ.prompt ?? anyQ.text ?? anyQ.question ?? anyQ.stem ?? 'Vraag niet beschikbaar';
}
export function ReviewAnswersScreen({
  sessionAnswers,
  sessionQuestions,
  initialIndex,
  onBack
}: ReviewAnswersScreenProps) {
  // Index of the currently expanded item. By default: first incorrect, or initialIndex.
  const firstIncorrect = sessionAnswers.findIndex(a => !a.isCorrect);
  const [openIndices, setOpenIndices] = useState<Set<number>>(() => {
    const s = new Set<number>();
    sessionAnswers.forEach((a, i) => {
      if (!a.isCorrect) s.add(i);
    });
    if (typeof initialIndex === 'number') s.add(initialIndex);
    return s;
  });
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const backBtnRef = useRef<HTMLButtonElement | null>(null);
  // Escape closes the review overlay
  useEscapeKey(onBack);
  // Lock body scroll while overlay is open
  useBodyScrollLock(true);
  // Move keyboard focus to the back button on open for screen-reader / kbd users
  useEffect(() => {
    backBtnRef.current?.focus({
      preventScroll: true
    });
  }, []);
  // Scroll to initialIndex (or first incorrect) on mount
  useEffect(() => {
    const target = typeof initialIndex === 'number' ? initialIndex : firstIncorrect >= 0 ? firstIncorrect : 0;
    const el = itemRefs.current[target];
    if (el) {
      // Defer to allow layout
      requestAnimationFrame(() => {
        el.scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      });
    }
    // Mount-only: auto-scroll to the first incorrect answer once when the
    // review screen opens. Re-running on items/answers churn would yank
    // the user's scroll position while they review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = (i: number) => {
    setOpenIndices(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);else next.add(i);
      return next;
    });
  };
  const incorrectCount = sessionAnswers.filter(a => !a.isCorrect).length;
  const totalCount = sessionAnswers.length;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 8
  }} transition={{
    duration: 0.25,
    ease: 'easeOut'
  }} className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" role="dialog" aria-modal="true" aria-label="Antwoorden bekijken" data-id="element-11483">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-100 bg-white/90 px-4 py-3 backdrop-blur sm:px-6" data-id="element-11484">
        <button ref={backBtnRef} type="button" onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400" aria-label="Terug naar samenvatting" data-id="element-11485">
          <ArrowLeft className="h-5 w-5" data-id="element-11486" />
        </button>
        <div className="min-w-0 flex-1" data-id="element-11487">
          <h2 className="truncate text-base font-bold text-slate-800 sm:text-lg" data-id="element-11488">
            Antwoorden bekijken
          </h2>
          <p className="truncate text-xs text-slate-500" data-id="element-11489">
            {incorrectCount > 0 ? `${incorrectCount} fout · ${totalCount - incorrectCount} goed` : 'Alles goed!'}
          </p>
        </div>
      </header>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-28 sm:px-6" data-id="element-11490">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-2.5" data-id="element-11491">
          <AnimatePresence initial={false} data-id="element-11492">
            {sessionAnswers.map((answer, i) => {
            const question = sessionQuestions.find(q => q.id === answer.questionId);
            const isOpen = openIndices.has(i);
            const correct = answer.isCorrect;
            return <motion.div key={`${answer.questionId}-${i}`} ref={el => itemRefs.current[i] = el} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: Math.min(i * 0.025, 0.3)
            }} className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${correct ? 'border-emerald-100' : 'border-rose-100'}`} data-id="element-11493">
                  <button type="button" onClick={() => toggle(i)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50" aria-expanded={isOpen} data-id="element-11494">
                    {/* Status dot */}
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${correct ? 'bg-emerald-50 text-emerald-500' : 'bg-rose-50 text-rose-500'}`} data-id="element-11495">
                      {correct ? <CheckCircle2 className="h-5 w-5" data-id="element-11496" /> : <XCircle className="h-5 w-5" data-id="element-11497" />}
                    </span>

                    {/* Index + preview */}
                    <span className="flex min-w-0 flex-1 items-baseline gap-2" data-id="element-11498">
                      <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-400" data-id="element-11499">
                        {i + 1}.
                      </span>
                      <span className="truncate text-sm font-medium text-slate-700" data-id="element-11500">
                        {getQuestionText(question)}
                      </span>
                    </span>

                    <motion.span animate={{
                  rotate: isOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} className="shrink-0 text-slate-300" data-id="element-11501">
                      <ChevronDown className="h-4 w-4" data-id="element-11502" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false} data-id="element-11503">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="overflow-hidden" data-id="element-11504">
                        <div className="space-y-3 border-t border-slate-100 px-4 py-3 text-sm" data-id="element-11505">
                          {/* Question text */}
                          <div data-id="element-11506">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-11507">
                              Vraag
                            </p>
                            <p className="mt-1 text-slate-700" data-id="element-11508">
                              {getQuestionText(question)}
                            </p>
                          </div>

                          {/* Correct answer (always shown when expanded) */}
                          {(() => {
                      const correctAns = getCorrectAnswerText(question);
                      if (!correctAns) return null;
                      return <div data-id="element-11509">
                                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500" data-id="element-11510">
                                  Juiste antwoord
                                </p>
                                <p className="mt-1 font-medium text-emerald-700" data-id="element-11511">
                                  {correctAns}
                                </p>
                              </div>;
                    })()}

                          {/* For incorrect: gentle prompt */}
                          {!correct && <div className="rounded-xl bg-rose-50/60 px-3 py-2 text-xs text-rose-700" data-id="element-11512">
                              Je had deze niet goed — geen zorgen, je kunt 'm
                              straks opnieuw oefenen.
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-11513">
        <div className="mx-auto flex w-full max-w-2xl" data-id="element-11514">
          <button type="button" onClick={onBack} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-slate-200 transition-transform active:scale-[0.98]" data-id="element-11515">
            <ArrowLeft className="h-4 w-4" data-id="element-11516" />
            Terug naar samenvatting
          </button>
        </div>
      </div>
    </motion.div>;
}